import moment from "moment";
import { ListTableColumn } from "../../../core";
import React, { ReactElement } from "react";

type ColumnKey =
    | 'leadIntent'
    | 'leadTitle'
    | 'leadType'
    | 'companyName'
    | 'contactName'
    | 'phone'
    | 'createdBy'
    | 'createdDateTime'
    | 'invoiceDate'
    | 'dueDate'
    | 'invoiceAmount'
    | 'sgst'
    | 'cgst'
    | 'netAmount'
    | 'clientName'
    | 'landlordName'
    | 'approvedBy'
    | 'approvedDateTime'
    | 'rejectedBy'
    | 'rejectedDateTime'
    | 'pendingAmount'
    | 'lastTransactionDate'
    | 'lastTransactionAmount'
    | 'landlordLastTransactionDate'
    | 'landlordLastTransactionAmount';

const commonColumns: Record<ColumnKey, ReactElement> = {
    leadIntent: (
        <ListTableColumn
            label="Lead Intent"
            source="leadDetails.leadIntent.name"
            column="leadIntent"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    leadTitle: (
        <ListTableColumn
            label="Lead Title"
            source="leadDetails.title"
            column="leadTitle"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    leadType: (
        <ListTableColumn
            label="Lead Type"
            source="leadDetails.leadType.name"
            column="leadType"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    companyName: (
        <ListTableColumn
            label="Company Name"
            source="leadDetails.company.name"
            column="name"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    contactName: (
        <ListTableColumn
            label="Contact Name"
            source="leadDetails.customer.fullName"
            column="fullName"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    phone: (
        <ListTableColumn
            label="Phone"
            source="leadDetails.customer"
            column="leadDetails.customer"
            formatContent={(data) => <span>{data.primaryPhone || data.phone}</span>}
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    createdBy: (
        <ListTableColumn
            label="Created By"
            source="createdBy.name"
            column="createdBy.name"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    createdDateTime: (
        <ListTableColumn
            label="Created Date & Time"
            source="document.createdAt"
            column="Created Date & Time"
            showDefault
            formatContent={(d) => moment(d).format("DD MMM YYYY hh:mm A")}
            sortable
            settingsGroup="Requirement"
        />
    ),
    invoiceDate: (
        <ListTableColumn
            label="Invoice Date"
            column="Invoice Date"
            source="document.paymentDetails.date"
            formatContent={(d) => moment(d).format("DD MMM YYYY")}
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    dueDate: (
        <ListTableColumn
            label="Due Date"
            column="Due Date"
            source="document.paymentDetails.dueDate"
            formatContent={(d) => (
                <span className={moment(d).endOf('day').isBefore(moment().endOf('day')) ? 'text-danger' : ''}>
                    {moment(d).format("DD MMM YYYY")}
                </span>
            )}
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    invoiceAmount: (
        <ListTableColumn
            label="Invoice Amount"
            source="document.paymentDetails.amount"
            formatContent={(value) => <span>₹{value}</span>}
            column="Invoice Amount"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    sgst: (
        <ListTableColumn
            label="SGST"
            source="document.paymentDetails.taxSgst"
            formatContent={(value) => <span>₹{value}</span>}
            column="SGST"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    cgst: (
        <ListTableColumn
            label="CGST"
            source="document.paymentDetails.taxCgst"
            formatContent={(value) => <span>₹{value}</span>}
            column="CGST"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    netAmount: (
        <ListTableColumn
            label="Net Amount"
            source="document.paymentDetails.netAmount"
            formatContent={(value) => <span>₹{value}</span>}
            column="Net Amount"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    clientName: (
        <ListTableColumn
            label="Client Name"
            source="document.clientApproval.sendTo"
            column="document.clientApproval.sendTo"
            formatContent={(sentTo) => <span>{sentTo?.map((element: { fullName: string }) => element.fullName)?.join(', ')}</span>}
            showDefault
            sortable
            settingsGroup="Client Name"
        />
    ),
    landlordName: (
        <ListTableColumn
            label="Landlord Name"
            source="seperateDocumentForLandlord.landlordApproval.sendTo"
            column="seperateDocumentForLandlord.landlordApproval.sendTo"
            formatContent={(sentTo) => <span>{sentTo?.map((element: { fullName: string }) => element.fullName)?.join(', ')}</span>}
            showDefault
            sortable
            settingsGroup="Landlord Name"
        />
    ),
    approvedBy: (
        <ListTableColumn
            label="Approved By"
            source="document.internalApproval.approvedBy.name"
            column="Approved By"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    approvedDateTime: (
        <ListTableColumn
            label="Approved Date & Time"
            source="document.internalApproval.approvedAt"
            column="Approved Date & Time"
            formatContent={(d) => moment(d).format("DD MMM YYYY hh:mm A")}
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    rejectedBy: (
        <ListTableColumn
            label="Rejected By"
            source="document.internalApproval.rejectedBy.name"
            column="Rejected By"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    rejectedDateTime: (
        <ListTableColumn
            label="Rejected At Date & Time"
            source="document.internalApproval.rejectedAt"
            column="Rejected At Date & Time"
            formatContent={(d) => moment(d).format("DD MMM YYYY hh:mm A")}
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    pendingAmount: (
        <ListTableColumn
            label="Pending Amount"
            source="document.paymentDetails"
            column="Pending Amount"
            showDefault
            formatContent={(value) => <span>₹{(value.netAmount - value.amountPaid)}</span>}
            sortable
            settingsGroup="Requirement"
        />
    ),
    lastTransactionDate: (
        <ListTableColumn
            label="Last Transaction Recorded On"
            source="document.paymentDetails.lastTransaction.createdAt"
            formatContent={(d) => moment(d).format("DD MMM YYYY hh:mm A")}
            column="Last Transaction Recorded On"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    lastTransactionAmount: (
        <ListTableColumn
            label="Last Transaction Amount "
            source="document.paymentDetails.lastTransaction.amountPaid"
            formatContent={(value) => <span>₹{value}</span>}
            column="Last Transaction Amount"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    landlordLastTransactionDate: (
        <ListTableColumn
            label="Last Transaction Recorded On"
            source="seperateDocumentForLandlord.paymentDetails.lastTransaction.createdAt"
            formatContent={(d) => moment(d).format("DD MMM YYYY hh:mm A")}
            column="Last Transaction Recorded On"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    ),
    landlordLastTransactionAmount: (
        <ListTableColumn
            label="Last Transaction Amount "
            source="seperateDocumentForLandlord.paymentDetails.lastTransaction.amountPaid"
            formatContent={(value) => <span>₹{value}</span>}
            column="Last Transaction Amount"
            showDefault
            sortable
            settingsGroup="Requirement"
        />
    )
};

const createColumnGroup = (columnKeys: ColumnKey[]) => columnKeys.map(key => commonColumns[key]);

export const WaitingForApprovalCustomer = createColumnGroup([
    'leadIntent', 'leadTitle', 'companyName', 'contactName', 'phone', 'createdBy',
    'createdDateTime', 'invoiceDate', 'dueDate', 'invoiceAmount', 'sgst',
    'cgst', 'netAmount'
]);

export const PaymentPendingCustomer = createColumnGroup([
    'leadIntent', 'leadTitle', 'leadType', 'companyName', 'clientName', 'contactName',
    'phone', 'createdBy', 'createdDateTime', 'approvedBy', 'approvedDateTime', 'invoiceDate',
    'dueDate', 'invoiceAmount', 'sgst', 'cgst', 'netAmount'
]);

export const PaymentCollectionCustomer = createColumnGroup([
    'leadIntent', 'leadTitle', 'leadType', 'companyName', 'clientName', 'contactName',
    'phone', 'createdBy', 'createdDateTime', 'pendingAmount', 'invoiceDate', 'dueDate',
    'invoiceAmount', 'sgst', 'cgst', 'netAmount', 'lastTransactionDate', 'lastTransactionAmount'
]);

export const RejectedCustomer = createColumnGroup([
    'leadIntent', 'leadTitle', 'leadType', 'companyName', 'contactName', 'phone',
    'createdBy', 'createdDateTime', 'rejectedBy', 'rejectedDateTime', 'invoiceDate',
    'dueDate', 'invoiceAmount', 'sgst', 'cgst', 'netAmount'
]);

export const WaitingForApprovalLandLord = createColumnGroup([
    'leadIntent', 'leadTitle', 'leadType', 'companyName', 'contactName', 'phone',
    'createdBy', 'createdDateTime', 'invoiceDate', 'dueDate', 'invoiceAmount',
    'sgst', 'cgst', 'netAmount'
]);

export const PaymentPendingLandlord = createColumnGroup([
    'leadIntent', 'leadTitle', 'leadType', 'companyName', 'landlordName', 'contactName',
    'phone', 'createdBy', 'createdDateTime', 'approvedBy', 'approvedDateTime', 'invoiceDate',
    'dueDate', 'invoiceAmount', 'sgst', 'cgst', 'netAmount'
]);

export const PaymentCollectionLandlord = createColumnGroup([
    'leadIntent', 'leadTitle', 'leadType', 'companyName', 'landlordName', 'contactName',
    'phone', 'createdBy', 'createdDateTime', 'pendingAmount', 'invoiceDate', 'dueDate',
    'invoiceAmount', 'sgst', 'cgst', 'netAmount', 'landlordLastTransactionDate', 'landlordLastTransactionAmount'
]);

export const RejectedLandlord = createColumnGroup([
    'leadIntent', 'leadTitle', 'leadType', 'companyName', 'contactName', 'phone',
    'createdBy', 'createdDateTime', 'rejectedBy', 'rejectedDateTime', 'invoiceDate',
    'dueDate', 'invoiceAmount', 'sgst', 'cgst', 'netAmount'
]);